/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/importVat.scss';

import React, {ChangeEventHandler, MouseEventHandler, useRef} from 'react';

import {useTranslation} from 'react-i18next';

import {
    AwesomeIcon,
    ChangeFunction,
    Checkbox,
    CloseButton,
    ErrorType,
    Link,
    Loader,
    WithBackground
} from '@dvrd/dvr-controls';

import {BolmateButton} from '../../../controls';

interface Props {
    onClose: MouseEventHandler;
    onDownloadTemplate: MouseEventHandler;
    onChangeFile: ChangeEventHandler<HTMLInputElement>;
    onSubmit: MouseEventHandler;
    onDownloadErrorFile: MouseEventHandler;
    onChangeApplyRetroactive: ChangeFunction<boolean>;
    error: ErrorType;
    importFile: File | null;
    loading: boolean;
    active: boolean;
    applyRetroactive: boolean;
}

export default function ImportVat(props: Props) {
    const {t} = useTranslation();
    const {
        importFile,
        onChangeFile,
        onDownloadTemplate,
        loading,
        onSubmit,
        active,
        onClose,
        onDownloadErrorFile,
        error,
        onChangeApplyRetroactive,
        applyRetroactive
    } = props;
    const inputRef = useRef<HTMLInputElement>(null);

    function onOpenFileSelection() {
        inputRef.current?.click();
    }

    function renderBody() {
        return (
            <div className='body-container'>
                <Loader active={loading} />
                <p className='info'>
                    {t('settings.vat.importInfo1', 'Je kunt hier de BTW percentages van producten in bulk uploaden.')}
                    <br />
                    <b>{t('settings.vat.importInfoOnlyEans', 'Alleen de EANs')}</b>{' '}
                    {t(
                        'settings.vat.importInfoRest',
                        'die je in dit bestand toevoegt worden herschreven – de rest van de ingestelde producten blijven hetzelfde.'
                    )}
                    <br />
                    <br />
                    {t(
                        'settings.vat.importInfoExample',
                        'Als je bijvoorbeeld een deel van de producten op 9% wilt zetten, hoef je alleen deze producten in de Excel te zetten.'
                    )}
                    <br />
                    <br />
                </p>
                <input type='file' ref={inputRef} style={{display: 'none'}} onChange={onChangeFile} accept='.xlsx' />
                <label className='sub-title'>{t('settings.vat.uploadImportFile', 'Importbestand uploaden')}</label>
                <div className='file-container' onClick={onOpenFileSelection}>
                    <AwesomeIcon name='file-excel' className='excel-icon' />
                    {renderFileLabel()}
                </div>
                <label className='info'>
                    <Link onClick={onDownloadTemplate}>
                        {t('settings.vat.downloadHere', 'Bekijk en download hier')}
                    </Link>{' '}
                    {t('settings.vat.exampleFormat', 'het voorbeeld van het juiste format.')}
                    <br />
                    {t(
                        'settings.vat.onlyXlsx',
                        'Upload enkel een .xlsx bestand (Excel standaard) in de opmaak uit het voorbeeld.'
                    )}
                    <br />
                    {t(
                        'settings.vat.canExport',
                        'Je kunt ook eerst jouw producten exporteren en het exportbestand gebruiken.'
                    )}
                </label>
                {renderOptions()}
                {renderError()}
            </div>
        );
    }

    function renderOptions() {
        return (
            <div className='options-container'>
                <Checkbox
                    checked={applyRetroactive}
                    onCheck={onChangeApplyRetroactive}
                    label={t('settings.vat.applyToExisting', 'Toepassen op reeds bestaande facturen')}
                />
            </div>
        );
    }

    function renderError() {
        if (!error) return null;
        return (
            <div className='error-container'>
                <label className='sub-title'>
                    {t('settings.vat.importDoneWithErrors', 'Import voltooid, met fouten')}
                </label>
                <p className='error'>{error}</p>
                <div className='file-container' onClick={onDownloadErrorFile}>
                    <AwesomeIcon name='file-excel' className='excel-icon' />
                    <label className='file-name'>import-fouten.xlsx</label>
                </div>
            </div>
        );
    }

    function renderFileLabel() {
        const fileName = importFile?.name ?? t('settings.vat.chooseXlsx', 'Kies een .xlsx bestand om te uploaden...');
        return <label className='file-name'>{fileName}</label>;
    }

    return (
        <WithBackground active={active} onClose={onClose}>
            <div className='import-vat default-popup'>
                <div className='header-container'>
                    <label className='header-title'>
                        {t('settings.vat.importVatTitle', 'BTW percentages importeren')}
                    </label>
                    <CloseButton onClick={onClose} />
                </div>
                {renderBody()}
                <div className='footer-container btn-2'>
                    {error ? (
                        <BolmateButton onClick={onClose} label={t('settings.vat.ok', 'Oke')} />
                    ) : (
                        <>
                            <BolmateButton
                                onClick={onClose}
                                label={t('settings.vat.cancel', 'Annuleren')}
                                disabled={loading}
                                secondary
                            />
                            <BolmateButton
                                onClick={onSubmit}
                                label={t('settings.vat.upload', 'Uploaden')}
                                disabled={loading || !importFile}
                            />
                        </>
                    )}
                </div>
            </div>
        </WithBackground>
    );
}
